// const BASE_DOMAIN =
// "https://australia-southeast1-staible-returns.cloudfunctions.net/staible-returns";
const BASE_DOMAIN = "https://lexi";
const BASE_DOMAIN_CALL_STRIPE = "-call-stripe";
const BASE_DOMAIN_SEND_VERIFICATION = "-send-verification";
const BASE_DOMAIN_VERIFY_PURCHASE = "-verify-purchase";
const BASE_DOMAIN_SUFFIX = "-mcdr62tc5a-ts.a.run.app";

// https://lexi-dev-send-verification-mcdr62tc5a-ts.a.run.app
// https://lexi-dev-verify-purchase-mcdr62tc5a-ts.a.run.app

// Determine environment suffix based on hostname or environment variable
function getEnvironmentSuffix() {
  // Allow override through environment variable
  if (import.meta.env.VITE_GCP_ENV) {
    return `-${import.meta.env.VITE_GCP_ENV}`;
  }

  const hostname = window.location.hostname;

  // Development environments
  if (hostname === "localhost" || hostname === "dev.lexi.tips") {
    return "-dev";
  }

  // Staging environment
  if (hostname === "staging.lexi.tips") {
    return "-staging";
  }

  // Production environment
  if (hostname === "lexi.tips") {
    return "-prod";
  }

  // Default to dev for safety
  console.warn("Unknown hostname, defaulting to dev environment");
  return "-dev";
}

const GCP_BASE_URL = `${BASE_DOMAIN}${getEnvironmentSuffix()}${BASE_DOMAIN_SUFFIX}`;
const GCP_BASE_CALL_STRIPE_URL = `${BASE_DOMAIN}${getEnvironmentSuffix()}${BASE_DOMAIN_CALL_STRIPE}${BASE_DOMAIN_SUFFIX}`;
const GCP_BASE_SEND_VERIFICATION_URL = `${BASE_DOMAIN}${getEnvironmentSuffix()}${BASE_DOMAIN_SEND_VERIFICATION}${BASE_DOMAIN_SUFFIX}`;
const GCP_BASE_VERIFY_PURCHASE_URL = `${BASE_DOMAIN}${getEnvironmentSuffix()}${BASE_DOMAIN_VERIFY_PURCHASE}${BASE_DOMAIN_SUFFIX}`;

const headers = {
  "Content-Type": "application/json",
};

// Plan IDs from the README
export const PRICE_IDS = {
  FREEMIUM: "price_1R3tIcQ6tDwB9QnD2J7VNMTq",
  BASIC_MONTHLY: "price_1R3tBpQ6tDwB9QnDVauPqKJX",
  PREMIUM_MONTHLY: "price_1R3tBpQ6tDwB9QnDvV29T2A9",
};

export function getStripeSessionHeaders() {
  const token = sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
  if (!token) throw new Error("Your session has expired. Please sign in again.");
  return { ...headers, Authorization: `Bearer ${token}` };
}

export async function getCustomerDetails() {
  const response = await fetch(GCP_BASE_CALL_STRIPE_URL, {
    method: "POST",
    headers: getStripeSessionHeaders(),
    body: JSON.stringify({ action: "verifyAndGetCustomer" }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw Object.assign(new Error(error.error || "Failed to fetch customer details"), {
      code: error.code,
      status: response.status,
    });
  }
  const result = await response.json();
  if (result.success !== true || !result.data ||
      (result.data.billingStatus !== "NO_BILLING_CUSTOMER" &&
       (typeof result.data.customerId !== "string" || !/^cus_[A-Za-z0-9]+$/.test(result.data.customerId)))) {
    throw new Error("Unable to confirm your plan. Please try again.");
  }
  return result.data;
}

export const getCustomerSubscriptions = async (customerId) => {
  try {
    const response = await fetch(`${GCP_BASE_CALL_STRIPE_URL}`, {
      method: "POST",
      headers: getStripeSessionHeaders(),
      body: JSON.stringify({
        action: "getSubscriptions",
        customerId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch subscription details");
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error("Error fetching subscription details:", error);
    throw error;
  }
};

export const updateCustomerEmail = async (customerId, email) => {
  try {
    const response = await fetch(`${GCP_BASE_CALL_STRIPE_URL}`, {
      method: "POST",
      headers: getStripeSessionHeaders(),
      body: JSON.stringify({
        action: "updateEmail",
        customerId,
        email,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update email");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating email:", error);
    throw error;
  }
};

export const updateCustomerPhone = async (customerId, phoneNumber) => {
  try {
    const response = await fetch(`${GCP_BASE_CALL_STRIPE_URL}`, {
      method: "POST",
      headers: getStripeSessionHeaders(),
      body: JSON.stringify({
        action: "updateBillingDetails",
        customerId,
        billingDetails: {
          phone: phoneNumber,
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update phone number");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating phone number:", error);
    throw error;
  }
};

/**
 * Update prospect data in Firestore via the stripe-customer API
 * @param {string} firebaseId - The Firebase User ID
 * @param {Object} data - The data to update (email, mobile)
 * @returns {Promise<Object>} The API response
 */
export const updateProspect = async (firebaseId, { email, mobile }) => {
  try {
    const response = await fetch("/api/stripe-customer", {
      method: "POST",
      headers: getStripeSessionHeaders(),
      body: JSON.stringify({
        action: "update-prospect",
        firebaseId,
        email,
        mobile,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update prospect");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating prospect:", error);
    throw error;
  }
};

/**
 * Update Stripe customer profile via the stripe-customer API
 * @param {string} customerId - The Stripe Customer ID
 * @param {Object} data - The data to update (email, mobile)
 * @returns {Promise<Object>} The API response
 */
export const updateStripeCustomerProfile = async (customerId, { email, mobile }) => {
  try {
    const response = await fetch("/api/stripe-customer", {
      method: "POST",
      headers: getStripeSessionHeaders(),
      body: JSON.stringify({
        action: "update",
        customerId,
        email,
        mobile,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update Stripe customer");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating Stripe customer profile:", error);
    throw error;
  }
};

export const cancelSubscription = async (customerId, immediate = false) => {
  try {
    const response = await fetch(`${GCP_BASE_CALL_STRIPE_URL}`, {
      method: "POST",
      headers: getStripeSessionHeaders(),
      body: JSON.stringify({
        action: "manageSubscription",
        customerId,
        subscriptionAction: "cancel",
        immediate,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to cancel subscription");
    }

    return await response.json();
  } catch (error) {
    console.error("Error canceling subscription:", error);
    throw error;
  }
};

export const upgradeSubscription = async (customerId, newPriceId) => {
  try {
    const response = await fetch(`${GCP_BASE_CALL_STRIPE_URL}`, {
      method: "POST",
      headers: getStripeSessionHeaders(),
      body: JSON.stringify({
        action: "upgradeSubscription",
        customerId,
        newPriceId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to upgrade subscription");
    }

    return await response.json();
  } catch (error) {
    console.error("Error upgrading subscription:", error);
    throw error;
  }
};

export const downgradeSubscription = async (customerId, newPriceId) => {
  try {
    const response = await fetch(`${GCP_BASE_CALL_STRIPE_URL}`, {
      method: "POST",
      headers: getStripeSessionHeaders(),
      body: JSON.stringify({
        action: "manageSubscription",
        customerId,
        subscriptionAction: "downgrade",
        newPriceId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to downgrade subscription");
    }

    return await response.json();
  } catch (error) {
    console.error("Error downgrading subscription:", error);
    throw error;
  }
};

export const sendVerificationCode = async (email, phoneNumber) => {
  try {
    const response = await fetch(`${GCP_BASE_SEND_VERIFICATION_URL}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ email, phoneNumber }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to send verification code");
    }

    return await response.json();
  } catch (error) {
    console.error("Error sending verification code:", error);
    throw error;
  }
};

export const verifyCode = async (phoneNumber, code) => {
  try {
    const response = await fetch(`${GCP_BASE_VERIFY_PURCHASE_URL}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ phoneNumber, code }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to verify code");
    }

    return await response.json();
  } catch (error) {
    console.error("Error verifying code:", error);
    throw error;
  }
};
